import { queryOptions } from '@tanstack/react-query'
import type { Company } from 'models/company'
import type { Project } from 'models/project'
import type { TradingMarketData } from 'models/trading'
import type { StockMarketBar } from 'models/chart'
import {
  fetchBmnMined,
  fetchExchangeRates,
  fetchTradingMarketData,
  fetchNavPrices,
  fetchStockmarketPrices,
  fetchXautPrice,
} from 'api/prices'
import type { RawRates } from 'models/investCurrency'
import {
  toUnitsOutstanding,
  type BmnMinedRow,
  type UnitsOutstanding,
  type NavValue,
  type TokenSupplyRow,
} from 'models/priceChart'
import { fetchStoProjectContent } from 'api/projectContent'
import type { ProjectContentItem } from 'models/projectContent'
import { normalizeProjectDocuments, type ProjectDocument } from 'models/projectDocument'
import { fetchCompany, fetchProjectDocuments, fetchPublicProjectByName, fetchTokenSupply } from 'api/projects'
import { fetchTokenBalances } from 'api/investment'
import type { TokenBalance } from 'models/investment'

/**
 * Every endpoint the STO details page touches, in one file.
 *
 * Definitions are centralised so the page's data surface is auditable in one place;
 * execution is distributed — each section hook calls the option it needs and the
 * cache dedupes. There is no page-level data context.
 *
 * Keys are namespaced under `sto` and deliberately separate from `hooks/queryKeys.ts`,
 * which the homepage shares. See docs/STO-ARCHITECTURE.md §5 and §10.
 */

export const STO_QUERY_NAMESPACE = 'sto' as const

export const stoQueryKeys = {
  all: [STO_QUERY_NAMESPACE] as const,
  project: (slug: string) => [STO_QUERY_NAMESPACE, 'project', slug] as const,
  content: (projectId: string) => [STO_QUERY_NAMESPACE, 'content', projectId] as const,
  company: (companyId: string) => [STO_QUERY_NAMESPACE, 'company', companyId] as const,
  documents: (projectId: string) => [STO_QUERY_NAMESPACE, 'documents', projectId] as const,
  tradingMarket: (assetId: string) => [STO_QUERY_NAMESPACE, 'trading-market', assetId] as const,
  underlyingStock: (symbol: string) => [STO_QUERY_NAMESPACE, 'underlying-stock', symbol] as const,
  nav: (assetId: string) => [STO_QUERY_NAMESPACE, 'nav', assetId] as const,
  bmnMined: (assetId: string) => [STO_QUERY_NAMESPACE, 'bmn-mined', assetId] as const,
  tokenSupply: (projectId: string, from: string) => [STO_QUERY_NAMESPACE, 'token-supply', projectId, from] as const,
  unitsOutstanding: (projectId: string, from: string) =>
    [STO_QUERY_NAMESPACE, 'units-outstanding', projectId, from] as const,
  tokenBalances: (userId: string) => [STO_QUERY_NAMESPACE, 'token-balances', userId] as const,
  exchangeRates: [STO_QUERY_NAMESPACE, 'exchange-rates'] as const,
  xautPrice: (currency: string) => [STO_QUERY_NAMESPACE, 'xaut-price', currency] as const,
}

const normalizeSlug = (slug: string): string => slug.trim().toLowerCase()

export const stoQueries = {
  /**
   * Root dependency. Supplies the id every other query needs, plus type, status and
   * the page metadata — nothing else may fetch before it resolves.
   */
  project: (slug: string) =>
    queryOptions({
      queryKey: stoQueryKeys.project(normalizeSlug(slug)),
      queryFn: async (): Promise<Project> => {
        const project = await fetchPublicProjectByName(normalizeSlug(slug))
        if (!project) throw new Error(`Project not found: ${slug}`)
        return project
      },
      enabled: Boolean(normalizeSlug(slug)),
    }),

  /** One request returns every content type, so content-driven sections resolve together. */
  content: (projectId: string | undefined) =>
    queryOptions({
      queryKey: stoQueryKeys.content(projectId ?? ''),
      queryFn: (): Promise<ProjectContentItem[]> => fetchStoProjectContent(projectId as string),
      enabled: Boolean(projectId),
    }),

  company: (companyId: string | undefined) =>
    queryOptions({
      queryKey: stoQueryKeys.company(companyId ?? ''),
      queryFn: (): Promise<Company | null> => fetchCompany(companyId as string),
      enabled: Boolean(companyId),
    }),

  documents: (projectId: string | undefined, projectName: string | undefined) =>
    queryOptions({
      queryKey: stoQueryKeys.documents(projectId ?? ''),
      queryFn: async (): Promise<ProjectDocument[]> => {
        const documents = await fetchProjectDocuments(projectId as string)
        return normalizeProjectDocuments(documents, projectName ?? '')
      },
      enabled: Boolean(projectId),
    }),

  /** Live exchange feed (30-day prices + top of the order book). Client-side only — never prefetched. */
  tradingMarket: (assetId: string | undefined, enabled = true) =>
    queryOptions({
      queryKey: stoQueryKeys.tradingMarket(assetId ?? ''),
      queryFn: (): Promise<TradingMarketData> => fetchTradingMarketData(assetId as string),
      enabled: enabled && Boolean(assetId),
    }),
  /** Daily bars of an equity product's underlying stock. Client-side only — never prefetched. */
  underlyingStock: (symbol: string | undefined) =>
    queryOptions({
      queryKey: stoQueryKeys.underlyingStock(symbol ?? ''),
      queryFn: (): Promise<StockMarketBar[]> => fetchStockmarketPrices(symbol as string),
      enabled: Boolean(symbol),
    }),
  /** A fund's monthly unit NAV, keyed by its Liquid asset. */
  nav: (assetId: string | undefined) =>
    queryOptions({
      queryKey: stoQueryKeys.nav(assetId ?? ''),
      queryFn: (): Promise<NavValue[]> => fetchNavPrices(assetId as string),
      enabled: Boolean(assetId),
    }),
  /** A mining note's daily mined BTC (BMN1, BMN2), keyed by its Liquid asset. */
  bmnMined: (assetId: string | undefined) =>
    queryOptions({
      queryKey: stoQueryKeys.bmnMined(assetId ?? ''),
      queryFn: (): Promise<BmnMinedRow[]> => fetchBmnMined(assetId as string),
      enabled: Boolean(assetId),
    }),
  /**
   * Tokens in circulation per day, from `from` (`YYYY-MM-DD`) to today: one call per use, never per
   * range (the backend asks for few requests) — a chart's whole history, or today alone for a count.
   * Client-side only.
   */
  tokenSupply: (projectId: string | undefined, from: string | undefined) =>
    queryOptions({
      queryKey: stoQueryKeys.tokenSupply(projectId ?? '', from ?? ''),
      queryFn: (): Promise<TokenSupplyRow[]> =>
        fetchTokenSupply(projectId as string, from as string, new Date().toISOString().slice(0, 10)),
      enabled: Boolean(projectId && from),
      staleTime: 60 * 60 * 1000,
    }),
  /**
   * A fund's units outstanding from its first NAV day (`from`) to today, kept where they change:
   * with the unit NAV, its total NAV over time. One wide call (the endpoint is costly), small to
   * keep and to server-render.
   */
  unitsOutstanding: (projectId: string | undefined, from: string | undefined) =>
    queryOptions({
      queryKey: stoQueryKeys.unitsOutstanding(projectId ?? '', from ?? ''),
      queryFn: async (): Promise<UnitsOutstanding | null> =>
        toUnitsOutstanding(
          await fetchTokenSupply(projectId as string, from as string, new Date().toISOString().slice(0, 10)),
        ) ?? null,
      enabled: Boolean(projectId && from),
      staleTime: 60 * 60 * 1000,
    }),
  /** Today's XAUt price in `currency`, to show a gold fund's amounts in XAUt. Client-side only. */
  xautPrice: (currency: string | undefined) =>
    queryOptions({
      queryKey: stoQueryKeys.xautPrice(currency ?? ''),
      queryFn: (): Promise<number | undefined> =>
        fetchXautPrice(currency as string, new Date().toISOString().slice(0, 10)),
      enabled: Boolean(currency),
      staleTime: 60 * 60 * 1000,
    }),
  /** Live exchange rates, for paying in another currency than the project's. Client-side only. */
  exchangeRates: (enabled: boolean) =>
    queryOptions({
      queryKey: stoQueryKeys.exchangeRates,
      queryFn: (): Promise<RawRates> => fetchExchangeRates(),
      enabled,
    }),
  /** The signed-in user's holdings. Keyed by user, so signing out or in never shows another's. */
  tokenBalances: (userId: string | undefined) =>
    queryOptions({
      queryKey: stoQueryKeys.tokenBalances(userId ?? ''),
      queryFn: (): Promise<TokenBalance[]> => fetchTokenBalances(),
      enabled: Boolean(userId),
    }),
}
